import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import FilterPopover from '../../community/residents/components/FilterPopover.jsx';

/**
 * Column definitions for the wandering (Accutech) devices table (7 columns).
 */
export const WANDERING_COLUMN_DEFS = [
  { key: 'device_name', label: 'Name', sortable: true, filterable: true },
  { key: 'descriptions', label: 'Description', sortable: true, filterable: true },
  { key: 'model_name', label: 'Model', sortable: true, filterable: true },
  { key: 'manufacture_uuid', label: 'Badge Number', sortable: true, filterable: true },
  { key: 'resident_name', label: 'Resident', sortable: true, filterable: true },
  { key: 'area_name', label: 'Area', sortable: true, filterable: true },
  { key: 'is_active', label: 'Active Status', sortable: true, filterable: true },
];

/**
 * WanderingDevicesTable — full data table for wandering/Accutech devices with
 * sorting, filtering, sticky header, row selection, and row actions.
 */
const WanderingDevicesTable = ({
  data,
  allData,
  visibleColumns,
  sortColumn,
  sortOrder,
  onSort,
  filters,
  onFilterChange,
  selectedIds,
  onSelectRow,
  onSelectAll,
  onUnassignDevice,
  onDeleteDevice,
  editRights,
  deleteRights,
}) => {
  const activeColumns = WANDERING_COLUMN_DEFS.filter((col) =>
    visibleColumns.includes(col.key)
  );

  /** Collect unique filter values from the full (unfiltered) dataset */
  const getFilterValues = (columnKey) => {
    const values = new Set();
    (allData || data).forEach((row) => {
      const val = row[columnKey];
      values.add(val != null && val !== '' ? String(val) : '(empty)');
    });
    return [...values].sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
    );
  };

  const showCheckbox = editRights || deleteRights;
  const allSelected =
    data.length > 0 && data.every((r) => selectedIds.includes(r.device_id));
  const someSelected = selectedIds.length > 0 && !allSelected;

  const renderSortIcon = (columnKey) => {
    if (sortColumn !== columnKey) {
      return (
        <svg
          className="dp-icon-sm dp-sort-icon dp-sort-icon--neutral"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={2}
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M8.25 15L12 18.75 15.75 15m-7.5-6L12 5.25 15.75 9"
          />
        </svg>
      );
    }
    return sortOrder === 'asc' ? (
      <svg
        className="dp-icon-sm dp-sort-icon dp-sort-icon--active"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={2}
        stroke="currentColor"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M4.5 15.75l7.5-7.5 7.5 7.5"
        />
      </svg>
    ) : (
      <svg
        className="dp-icon-sm dp-sort-icon dp-sort-icon--active"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth={2}
        stroke="currentColor"
        aria-hidden="true"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M19.5 8.25l-7.5 7.5-7.5-7.5"
        />
      </svg>
    );
  };

  /** Renders a single cell value */
  const renderCell = (row, columnKey) => {
    switch (columnKey) {
      case 'device_name':
        return row.manufacture_id === 6 || row.manufacture_id === '6' ? (
          <Link
            to={'/community/devices/6/' + row.device_id}
            className="dp-device-link"
          >
            {row.device_name}
          </Link>
        ) : (
          <span
            className="dp-cell-text"
            style={{ fontWeight: 'var(--font-weight-semibold)' }}
          >
            {row.device_name}
          </span>
        );

      case 'resident_name':
        return row.resident_name && row.resident_id ? (
          <div className="dp-resident-list">
            <Link
              to={'/community/residents/' + row.resident_id}
              className="dp-resident-badge"
            >
              {row.resident_name}
            </Link>
          </div>
        ) : (
          <span className="dp-cell-text--muted">&mdash;</span>
        );

      case 'area_name':
        return row.area_id ? (
          <Link to={'/community/areas/' + row.area_id} className="dp-cell-text dp-cell-text--link" style={{ textDecoration: 'none' }}>
            {row.area_name}
          </Link>
        ) : (
          <span className="dp-cell-text">{row.area_name || '—'}</span>
        );

      case 'is_active':
        return row.is_active ? (
          <span className="dp-status dp-status--active">
            <span className="dp-status__dot" aria-hidden="true" />
            Active
          </span>
        ) : (
          <span className="dp-status dp-status--inactive">
            <span className="dp-status__dot" aria-hidden="true" />
            Unassigned
          </span>
        );

      default:
        return (
          <span className="dp-cell-text">{row[columnKey] || '\u2014'}</span>
        );
    }
  };

  return (
    <div className="dp-table-wrap">
      <table className="dp-table">
        <thead className="dp-thead">
          <tr>
            {/* Checkbox column */}
            {showCheckbox ? (
              <th scope="col" className="dp-th dp-th--checkbox">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = someSelected;
                  }}
                  onChange={onSelectAll}
                  className="dp-checkbox"
                  aria-label="Select all devices"
                />
              </th>
            ) : null}

            {/* Actions column */}
            <th scope="col" className="dp-th dp-th--actions">
              <span className="dp-sr-only">Actions</span>
            </th>

            {/* Data columns */}
            {activeColumns.map((col) => (
              <th key={col.key} scope="col" className="dp-th">
                <div className="dp-th__content">
                  {col.sortable ? (
                    <button
                      type="button"
                      onClick={() => onSort(col.key)}
                      className="dp-th__sort-btn"
                      aria-label={`Sort by ${col.label}`}
                    >
                      {col.label}
                      {renderSortIcon(col.key)}
                    </button>
                  ) : (
                    <span>{col.label}</span>
                  )}

                  {col.filterable ? (
                    <FilterPopover
                      columnKey={col.filterKey || col.key}
                      label={col.label}
                      values={getFilterValues(col.filterKey || col.key)}
                      selectedValues={filters[col.filterKey || col.key] || []}
                      onChange={onFilterChange}
                      data={allData}
                    />
                  ) : null}
                </div>
              </th>
            ))}
          </tr>
        </thead>

        <tbody className="dp-tbody">
          {data.map((row) => {
            const isSelected = selectedIds.includes(row.device_id);
            return (
              <tr
                key={row.device_id}
                className={`dp-tr ${isSelected ? 'is-selected' : ''}`}
              >
                {/* Checkbox */}
                {showCheckbox ? (
                  <td className="dp-td dp-td--checkbox">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => onSelectRow(row.device_id)}
                      className="dp-checkbox"
                      aria-label={`Select ${row.device_name}`}
                    />
                  </td>
                ) : null}

                {/* Row actions kebab */}
                <td className="dp-td dp-td--actions">
                  <WanderingRowActionsMenu
                    device={row}
                    editRights={editRights}
                    deleteRights={deleteRights}
                    onUnassignDevice={onUnassignDevice}
                    onDeleteDevice={onDeleteDevice}
                  />
                </td>

                {/* Data cells */}
                {activeColumns.map((col) => (
                  <td key={col.key} className="dp-td">
                    {renderCell(row, col.key)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

/**
 * WanderingRowActionsMenu — kebab menu for per-row wandering device actions.
 */
const WanderingRowActionsMenu = ({
  device,
  editRights,
  deleteRights,
  onUnassignDevice,
  onDeleteDevice,
}) => {
  const [open, setOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState({});
  const menuRef = useRef(null);

  const showUnassign = editRights && device.is_active;
  const showDelete = deleteRights;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  // Close on scroll so the fixed-positioned menu doesn't detach from the
  // (moving) trigger button.
  useEffect(() => {
    if (!open) return;
    const onScroll = () => setOpen(false);
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [open]);

  // Compute viewport coordinates for the menu when opening. Using fixed
  // positioning escapes the table wrapper's overflow clipping that was
  // chopping off the menu in the Wandering tab.
  const handleToggle = () => {
    if (!open && menuRef.current) {
      const rect = menuRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      if (spaceBelow < 180) {
        setMenuStyle({
          position: 'fixed',
          bottom: `${window.innerHeight - rect.top + 4}px`,
          right: `${window.innerWidth - rect.right}px`,
          top: 'auto', left: 'auto', margin: 0, zIndex: 9999,
        });
      } else {
        setMenuStyle({
          position: 'fixed',
          top: `${rect.bottom + 4}px`,
          right: `${window.innerWidth - rect.right}px`,
          bottom: 'auto', left: 'auto', margin: 0, zIndex: 9999,
        });
      }
    }
    setOpen(!open);
  };

  // Don't render menu trigger if no actions are available
  if (!showUnassign && !showDelete) {
    return null;
  }

  return (
    <div className="dp-row-actions" ref={menuRef}>
      <button
        type="button"
        onClick={handleToggle}
        className="dp-row-actions__trigger"
        aria-label={`Actions for ${device.device_name}`}
        aria-expanded={open}
      >
        <i className="fe fe-more-vertical" aria-hidden="true" />
      </button>

      {open ? (
        <div className="dp-row-actions__menu" style={menuStyle}>
          {showUnassign ? (
            <button
              type="button"
              onClick={() => {
                onUnassignDevice(device);
                setOpen(false);
              }}
              className="dp-row-actions__item"
            >
              <svg
                className="dp-icon-md dp-row-actions__item-icon"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M7.5 10.5h.008v.008H7.5V10.5zm0 0a4.5 4.5 0 019 0m-9 0H3.75m13.5 0h.008v.008h-.008V10.5zm0 0H21.75m-3.75 0a4.5 4.5 0 00-9 0M12 3v1.5M12 19.5V21M4.22 4.22l1.06 1.06m13.44 13.44l1.06 1.06M1.5 12h1.5m16.5 0h1.5M4.22 19.78l1.06-1.06M18.72 5.28l1.06-1.06"
                />
              </svg>
              Unassign Device
            </button>
          ) : null}

          {showDelete ? (
            <button
              type="button"
              onClick={() => {
                onDeleteDevice(device);
                setOpen(false);
              }}
              className="dp-row-actions__item dp-row-actions__item--danger"
            >
              <svg
                className="dp-icon-md dp-row-actions__item-icon"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
                />
              </svg>
              Delete Device
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default WanderingDevicesTable;
